import {
  AccountTreeOutlined,
  AutoAwesomeOutlined,
  ExtensionOutlined,
  FactCheckOutlined,
  FolderCopyOutlined,
  GroupsOutlined,
  MeetingRoomOutlined,
  ShieldOutlined,
  TouchAppOutlined,
} from '@mui/icons-material';
import { Box, Chip, Container, Stack, Typography } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import React from 'react';

const MotionBox = motion.create(Box);

const capabilities = [
  {
    icon: <FolderCopyOutlined />,
    title: '业务上下文在一个工作台汇聚',
    description:
      '企业组织、项目资料、会议记录与流程数据形成完整上下文，Agent 不再只看到一段孤立的问题。',
    tags: ['项目资料', '企业制度', '历史记录'],
    wide: true,
  },
  {
    icon: <AccountTreeOutlined />,
    title: '理解任务并自动拆解',
    description: '把一句办公需求拆成可执行步骤，明确输入、动作、负责人和交付结果。',
  },
  {
    icon: <ExtensionOutlined />,
    title: '调用已有办公工具',
    description: '连接日程、审批、考勤、待办和文档能力，让 Agent 从建议走到实际操作。',
  },
  {
    icon: <MeetingRoomOutlined />,
    title: '会议结论继续被执行',
    description: '自动提炼决策、风险与行动项，把会议结果转成可跟进的工作。',
  },
  {
    icon: <FactCheckOutlined />,
    title: '每个结果都有依据',
    description: '回答和执行过程保留来源、工具调用与上下文，方便复核与追溯。',
  },
  {
    icon: <TouchAppOutlined />,
    title: '关键节点由人确认',
    description: '涉及发布、审批和高风险动作时暂停等待确认，不让自动化越过责任边界。',
    wide: true,
  },
];

const Features: React.FC = () => {
  const reduceMotion = useReducedMotion();

  return (
    <Box
      id="features"
      component="section"
      sx={{
        py: { xs: 9, md: 13 },
        backgroundColor: 'rgb(var(--surface-rgb))',
        borderTop: '1px solid rgba(var(--line-rgb), 0.07)',
      }}
    >
      <Container maxWidth="lg">
        <MotionBox
          {...(reduceMotion
            ? {}
            : {
                initial: { opacity: 1, y: 22 },
                whileInView: { opacity: 1, y: 0 },
                transition: { duration: 0.55 },
                viewport: { once: true, amount: 0.35 },
              })}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, .9fr) minmax(0, 1.1fr)' },
            gap: { xs: 2.5, md: 8 },
            alignItems: 'end',
            mb: { xs: 5, md: 7 },
          }}
        >
          <Box>
            <Typography
              sx={{
                color: 'rgb(var(--brand-rgb))',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.72rem',
                letterSpacing: '0.14em',
                mb: 1.5,
              }}
            >
              AGENT OFFICE CAPABILITIES
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: '2rem', md: '3.3rem' },
                fontWeight: 800,
                letterSpacing: '-0.045em',
                lineHeight: 1.12,
              }}
            >
              让 AI 从回答问题，
              <Box component="span" sx={{ display: 'block', color: 'rgb(var(--brand-rgb))' }}>
                走到完成工作。
              </Box>
            </Typography>
          </Box>
          <Typography sx={{ color: 'rgba(var(--ink-rgb), 0.64)', fontSize: '1rem', lineHeight: 1.85 }}>
            Agent 办公不是增加一个新入口，而是让智能体进入真实业务流程：
            知道事情发生在哪个项目、依据哪些材料、下一步该调用什么工具。
          </Typography>
        </MotionBox>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
            gap: 1.6,
          }}
        >
          {capabilities.map((capability, index) => (
            <MotionBox
              key={capability.title}
              {...(reduceMotion
                ? {}
                : {
                    initial: { opacity: 1, y: 20 },
                    whileInView: { opacity: 1, y: 0 },
                    transition: { duration: 0.42, delay: index * 0.055 },
                    viewport: { once: true, amount: 0.2 },
                  })}
              sx={{
                gridColumn: capability.wide ? { sm: 'span 2', lg: 'span 2' } : undefined,
                p: { xs: 2.5, md: capability.wide ? 3.5 : 2.8 },
                minHeight: capability.wide ? 280 : 230,
                borderRadius: '24px',
                position: 'relative',
                overflow: 'hidden',
                background:
                  capability.wide
                    ? 'linear-gradient(145deg, rgba(var(--brand-rgb), .13), rgba(var(--surface-rgb), .96) 68%)'
                    : 'rgba(var(--surface-soft-rgb), 0.60)',
                border: '1px solid rgba(var(--line-rgb), 0.09)',
                transition: 'transform .22s ease, border-color .22s ease, box-shadow .22s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  borderColor: 'rgba(var(--brand-rgb), 0.24)',
                  boxShadow: '0 18px 40px rgba(var(--shadow-rgb), 0.09)',
                },
                '&::after': capability.wide
                  ? {
                      content: '""',
                      position: 'absolute',
                      width: 220,
                      height: 220,
                      borderRadius: '50%',
                      right: -88,
                      bottom: -110,
                      border: '34px solid rgba(var(--brand-rgb), 0.08)',
                    }
                  : undefined,
              }}
            >
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: '14px',
                  display: 'grid',
                  placeItems: 'center',
                  color: 'rgb(var(--brand-rgb))',
                  backgroundColor: 'rgba(var(--brand-tint-rgb), 0.11)',
                  fontSize: 24,
                  mb: 2.2,
                }}
              >
                {capability.icon}
              </Box>
              <Typography component="h3" sx={{ fontWeight: 750, fontSize: { xs: '1.08rem', md: '1.2rem' }, mb: 1 }}>
                {capability.title}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(var(--ink-rgb), 0.62)',
                  fontSize: '0.88rem',
                  lineHeight: 1.75,
                  maxWidth: capability.wide ? 520 : 420,
                }}
              >
                {capability.description}
              </Typography>
              {capability.tags && (
                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 2.4 }}>
                  {capability.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{
                        color: 'rgba(var(--ink-rgb), 0.72)',
                        backgroundColor: 'rgba(var(--surface-rgb), 0.76)',
                        border: '1px solid rgba(var(--line-rgb), 0.08)',
                      }}
                    />
                  ))}
                </Stack>
              )}
            </MotionBox>
          ))}
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.2, sm: 3 }}
          sx={{
            mt: 2,
            p: { xs: 2.2, sm: 2.5 },
            borderRadius: '20px',
            backgroundColor: 'rgb(var(--ink-rgb))',
            color: 'rgb(var(--surface-rgb))',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flex: 1 }}>
            <AutoAwesomeOutlined sx={{ color: 'rgb(var(--accent-rgb))' }} />
            <Typography sx={{ fontWeight: 650, fontSize: '0.92rem' }}>
              面向工程企业：让项目资料、流程与 AI 执行连成一条线
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <GroupsOutlined sx={{ color: 'rgb(var(--accent-rgb))' }} />
            <Typography sx={{ fontSize: '0.84rem', opacity: 0.7 }}>
              组织、空间与项目边界清晰可控
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <ShieldOutlined sx={{ color: 'rgb(var(--accent-rgb))' }} />
            <Typography sx={{ fontSize: '0.84rem', opacity: 0.7 }}>
              权限前置校验
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default Features;
